// @ts-nocheck
import { AxiosError, AxiosResponse } from 'axios';
import axios from 'axios';
import {
  Configuration,
  AuthApi,
  PaymentsApi,
  MachinesApi,
  StoreApi,
  HuggingfaceApi,
  LocalAIProxyApi,
  RequestsApi,
  type ApiLoginInput,
  type ApiRegisterInput,
  type ApiMeOutput,
  type ApiChangePasswordInput,
  type ApiVerifyOTPInput,
  type ApiVerifyOTPOutput,
  type ApiAddressGetOutput,
  type ApiAddressPostInput,
  type ApiTrialBalanceGetOutput,
  type ApiTrialBalancePostInput,
  type MachinesMachines,
  type MachinesMachinePricing,
  type ApiRequestsCostInput,
  type ApiRequestsCostOutput,
  type ApiStoreHistoryPostInput,
  type RequestsRequestAppPost,
  type RequestsRequestHardwarePost,
  type RequestsRequestHardwareRentPost,
  type RequestsRequestHardwareRentOutPost,
  type RequestsRequestLLMBusinessPost,
  type RequestsRequestLLMPersonalPost,
  type RequestsRequestAgentPlacementPost,
  type StoreMessage,
  type RestErrResponse,
  type ModelsModel,
} from '../generated/api';
import type { ApiResponse } from '@/types';
import { ApiError } from '@/utils/errorHandling';
import { lazyApiClient } from './lazyApiClient';

/**
 * Enhanced error type that includes additional context from Axios errors
 */
interface EnhancedApiError {
  message: string;
  code?: number;
  status: number;
  context?: Record<string, unknown> | undefined;
  originalError?: unknown;
  isNetworkError: boolean;
  isValidationError: boolean;
  validationErrors?: Record<string, string[]> | undefined;
}

/**
 * Transform Axios errors to our existing ApiResponse error format with comprehensive error handling
 */
function transformGeneratedError(error: unknown): EnhancedApiError {
  console.error('Generated API Error:', error);

  // Handle Axios errors
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<RestErrResponse>;
    const response = axiosError.response;
    const request = axiosError.request;

    // Network error (no response received)
    if (!response && request) {
      return {
        message:
          'Network connection failed. Please check your internet connection.',
        code: 0,
        status: 0,
        context: undefined,
        originalError: error,
        isNetworkError: true,
        isValidationError: false,
      };
    }

    // Request timeout
    if (
      axiosError.code === 'ECONNABORTED' ||
      (axiosError.message && axiosError.message.includes('timeout'))
    ) {
      return {
        message: 'Request timeout. Please try again.',
        code: 408,
        status: 408,
        context: undefined,
        originalError: error,
        isNetworkError: true,
        isValidationError: false,
      };
    }

    // Response received with error status
    if (response) {
      const isValidationError =
        response.status === 400 || response.status === 422;
      let validationErrors: Record<string, string[]> | undefined;
      let errorMessage = 'An error occurred';

      // Parse structured error response
      if (response.data && typeof response.data === 'object') {
        const apiError = response.data as RestErrResponse;
        errorMessage =
          apiError.error || axiosError.message || 'API request failed';

        // Extract validation errors from context
        if (isValidationError && apiError.context) {
          validationErrors = extractValidationErrors(apiError.context);
        }
      } else {
        // Fallback to status text or generic message
        errorMessage =
          response.statusText || getDefaultErrorMessage(response.status);
      }

      return {
        message: errorMessage,
        code: response.data?.code || response.status,
        status: response.status,
        context: response.data?.context,
        originalError: error,
        isNetworkError: false,
        isValidationError,
        validationErrors,
      };
    }

    // Request setup error
    return {
      message: axiosError.message || 'Request configuration error',
      code: 0,
      status: 0,
      context: undefined,
      originalError: error,
      isNetworkError: false,
      isValidationError: false,
    };
  }

  // Handle generic errors
  if (error instanceof Error) {
    return {
      message: error.message || 'An unexpected error occurred',
      code: 0,
      status: 0,
      context: undefined,
      originalError: error,
      isNetworkError: false,
      isValidationError: false,
    };
  }

  // Fallback for unknown error types
  return {
    message: 'An unexpected error occurred',
    code: 0,
    status: 0,
    context: undefined,
    originalError: error,
    isNetworkError: false,
    isValidationError: false,
  };
}

/**
 * Field error interface for validation error parsing
 */
interface FieldError {
  field: string;
  message: string;
}

/**
 * Extract validation errors from API error context
 */
function extractValidationErrors(
  context: Record<string, unknown>
): Record<string, string[]> | undefined {
  // Handle different validation error formats that might be in the context
  if (
    context.validation_errors &&
    typeof context.validation_errors === 'object' &&
    context.validation_errors !== null
  ) {
    return context.validation_errors as Record<string, string[]>;
  }

  if (
    context.errors &&
    typeof context.errors === 'object' &&
    context.errors !== null
  ) {
    return context.errors as Record<string, string[]>;
  }

  if (
    context.fields &&
    typeof context.fields === 'object' &&
    context.fields !== null
  ) {
    return context.fields as Record<string, string[]>;
  }

  // Handle array of field errors
  if (context.field_errors && Array.isArray(context.field_errors)) {
    const fieldErrors: Record<string, string[]> = {};
    context.field_errors.forEach((fieldError: unknown) => {
      const error = fieldError as FieldError;
      if (
        error &&
        typeof error === 'object' &&
        'field' in error &&
        'message' in error &&
        typeof error.field === 'string' &&
        typeof error.message === 'string'
      ) {
        if (!fieldErrors[error.field]) {
          fieldErrors[error.field] = [];
        }
        fieldErrors[error.field]!.push(error.message);
      }
    });
    return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
  }

  return undefined;
}

/**
 * Get default error message based on HTTP status code
 */
function getDefaultErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Authentication required. Please log in again.';
    case 403:
      return "Access denied. You don't have permission to perform this action.";
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'Conflict. The resource already exists or is in use.';
    case 422:
      return 'Invalid data provided. Please check your input.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'Internal server error. Please try again later.';
    case 502:
      return 'Bad gateway. Service temporarily unavailable.';
    case 503:
      return 'Service temporarily unavailable. Please try again later.';
    case 504:
      return 'Gateway timeout. Please try again.';
    default:
      return status >= 500
        ? 'Server error. Please try again later.'
        : 'An error occurred. Please try again.';
  }
}

/**
 * Authentication middleware for handling JWT cookies and Bearer tokens
 */
class AuthenticationMiddleware {
  /**
   * Get Bearer token from localStorage
   */
  getBearerToken(): string | null {
    return localStorage.getItem('bearerToken');
  }

  /**
   * Check if we have authentication cookies
   */
  hasAuthCookies(): boolean {
    return document.cookie.includes('jwt=');
  }

  /**
   * Check if we have any form of authentication (cookies or Bearer token)
   */
  hasAuthentication(): boolean {
    return this.hasAuthCookies() || !!this.getBearerToken();
  }

  /**
   * Create configuration with authentication middleware
   */
  createConfiguration(): Configuration {
    // Use environment variable or fallback to production URL
    const baseURL = process.env.REACT_APP_API_URL || 'https://api.swarmind.ai';

    console.log('API Client Configuration:', {
      baseURL,
      env: process.env.NODE_ENV,
      apiUrl: process.env.REACT_APP_API_URL,
    });

    return new Configuration({
      basePath: baseURL,
      baseOptions: {
        // CRITICAL: Include credentials for JWT cookies - this is required by backend
        withCredentials: true,
        timeout: 30000, // 30 second timeout
        headers: {
          'Content-Type': 'application/json',
        },
      },
      // Access token function for Bearer token authentication (secondary auth method)
      accessToken: () => {
        const bearerToken = this.getBearerToken();
        return bearerToken || '';
      },
    });
  }

  /**
   * Store Bearer token from API response
   */
  storeBearerToken(token: string): void {
    localStorage.setItem('bearerToken', token);
  }

  /**
   * Clear stored authentication data
   */
  clearAuthData(): void {
    localStorage.removeItem('bearerToken');
    localStorage.removeItem('userData');
  }

  /**
   * Wait for cookies to be set by the browser
   */
  async waitForCookies(): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 100));
    console.log('Current cookies after auth operation:', document.cookie);
    console.log('JWT cookie present:', this.hasAuthCookies());
    console.log(
      'All document cookies:',
      document.cookie.split(';').map(c => c.trim())
    );

    // Check if we have any cookies at all
    if (!document.cookie) {
      console.warn(
        '⚠️ No cookies found at all - this might be a CORS or domain issue'
      );
    }
  }
}

/**
 * Integration wrapper that adapts the generated API client to existing patterns
 * Uses lazy loading for better performance and bundle size optimization
 */
export class SwarmindApiClient {
  private authMiddleware: AuthenticationMiddleware;
  private configuration: Configuration;
  private apiBaseURL: string;
  // Core APIs loaded immediately (frequently used)
  private authApi: AuthApi;
  private paymentsApi: PaymentsApi;
  private machinesApi: MachinesApi;
  // Less frequently used APIs loaded lazily
  private storeApi: StoreApi | null = null;
  private huggingfaceApi: HuggingfaceApi | null = null;
  private localAIProxyApi: LocalAIProxyApi | null = null;
  private requestsApi: RequestsApi | null = null;

  /**
   * Resolve potentially unstable generated operation names for payments-related calls.
   * The OpenAPI fix script may rename operationIds; this keeps the wrapper resilient.
   */
  private resolveTrialBalanceGet() {
    const api: any = this.paymentsApi as any;
    return (
      api.apiNewAPIServiceFunc6 ||
      api.trialBalanceGet ||
      api.getTrialBalance ||
      api.apiTrialBalanceGet
    );
  }

  private resolveSurveyPost() {
    const api: any = this.paymentsApi as any;
    return (
      api.apiNewAPIServiceFunc7 ||
      api.trialBalancePost ||
      api.submitSurvey ||
      api.apiTrialBalancePost
    );
  }
  /**
   * Resolve request endpoints. Prefer stable names from fix-openapi-operation-ids.js, then current generated numbers.
   * Generated RequestsApi: hardware_rent=15, hardware_rent_out=152, llm_business=153, llm_personal=154, agent_placement=155.
   */
  private resolveAppRequest() {
    const api: any = this.requestsApi as any;
    return (
      api.requestApp ||
      api.appRequest ||
      api.apiNewAPIServiceFunc132 ||
      api.apiNewAPIServiceFunc112
    );
  }

  private resolveHardwareRequest() {
    const api: any = this.requestsApi as any;
    return (
      api.requestHardwareRent ||
      api.hardwareRequest ||
      api.apiNewAPIServiceFunc15 ||
      api.apiNewAPIServiceFunc13 ||
      api.apiNewAPIServiceFunc12
    );
  }

  private resolveHardwareRentRequest() {
    const api: any = this.requestsApi as any;
    return (
      api.requestHardwareRent ||
      api.apiNewAPIServiceFunc15 ||
      api.hardwareRentRequest ||
      api.postRequestHardwareRent
    );
  }

  private resolveHardwareRentOutRequest() {
    const api: any = this.requestsApi as any;
    return (
      api.requestHardwareRentOut ||
      api.apiNewAPIServiceFunc152 ||
      api.hardwareRentOutRequest ||
      api.postRequestHardwareRentOut
    );
  }

  private resolveLLMBusinessRequest() {
    const api: any = this.requestsApi as any;
    return (
      api.requestLLMBusiness ||
      api.apiNewAPIServiceFunc153 ||
      api.llmBusinessRequest ||
      api.requestLlmBusiness ||
      api.postRequestLlmBusiness
    );
  }

  private resolveLLMPersonalRequest() {
    const api: any = this.requestsApi as any;
    return (
      api.requestLLMPersonal ||
      api.apiNewAPIServiceFunc154 ||
      api.llmPersonalRequest ||
      api.requestLlmPersonal ||
      api.postRequestLlmPersonal
    );
  }

  private resolveAgentPlacementRequest() {
    const api: any = this.requestsApi as any;
    return (
      api.requestAgentPlacement ||
      api.apiNewAPIServiceFunc155 ||
      api.agentPlacementRequest ||
      api.postRequestAgentPlacement
    );
  }

  constructor() {
    this.authMiddleware = new AuthenticationMiddleware();
    this.configuration = this.authMiddleware.createConfiguration();
    this.apiBaseURL = process.env.REACT_APP_API_URL || 'https://api.swarmind.ai';

    // Initialize core API instances immediately (auth, payments, machines are used frequently)
    this.authApi = new AuthApi(this.configuration);
    this.paymentsApi = new PaymentsApi(this.configuration);
    this.machinesApi = new MachinesApi(this.configuration);

    // Less frequently used APIs will be loaded lazily
  }

  /**
   * Get Store API instance (lazy loaded)
   */
  private async getStoreApi(): Promise<StoreApi> {
    if (!this.storeApi) {
      this.storeApi = await lazyApiClient.getStoreApi();
    }
    return this.storeApi;
  }

  /**
   * Get Huggingface API instance (lazy loaded)
   */
  private async getHuggingfaceApi(): Promise<HuggingfaceApi> {
    if (!this.huggingfaceApi) {
      this.huggingfaceApi = await lazyApiClient.getHuggingfaceApi();
    }
    return this.huggingfaceApi;
  }

  /**
   * Get LocalAI Proxy API instance (lazy loaded)
   */
  private async getLocalAIProxyApi(): Promise<LocalAIProxyApi> {
    if (!this.localAIProxyApi) {
      this.localAIProxyApi = await lazyApiClient.getLocalAIProxyApi();
    }
    return this.localAIProxyApi;
  }

  /**
   * Get Requests API instance (lazy loaded)
   */
  private async getRequestsApi(): Promise<RequestsApi> {
    if (!this.requestsApi) {
      this.requestsApi = await lazyApiClient.getRequestsApi();
    }
    return this.requestsApi;
  }

  /**
   * Generic wrapper method that transforms generated API responses to existing ApiResponse format
   */
  private async wrapApiCall<T>(
    apiCall: () => Promise<AxiosResponse<T>>
  ): Promise<ApiResponse<T>> {
    try {
      const response = await apiCall();

      // Debug logging
      console.log('API call successful:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
      });

      // Debug cookie headers for login requests
      if (response.config?.url?.includes('/login')) {
        console.log('🍪 Login response headers:', response.headers);
        console.log('🍪 Set-Cookie header:', response.headers['set-cookie']);
        console.log('🍪 Current domain:', window.location.hostname);
        console.log('🍪 Current protocol:', window.location.protocol);
      }

      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      const transformedError = transformGeneratedError(error);

      return {
        error: transformedError.message,
        status: transformedError.status,
      };
    }
  }

  /**
   * Generic wrapper method that throws ApiError instances for ErrorBoundary integration
   */
  private async wrapApiCallWithThrow<T>(
    apiCall: () => Promise<AxiosResponse<T>>
  ): Promise<T> {
    try {
      const response = await apiCall();

      // Debug logging
      console.log('API call successful:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
      });

      return response.data;
    } catch (error) {
      const transformedError = transformGeneratedError(error);

      // Create and throw ApiError for ErrorBoundary to catch
      const apiError = new ApiError(
        transformedError.message,
        transformedError.status,
        transformedError.code?.toString(),
        transformedError.context,
        transformedError.validationErrors
      );

      // Set additional properties
      apiError.isNetworkError = transformedError.isNetworkError;
      apiError.isValidationError = transformedError.isValidationError;

      throw apiError;
    }
  }

  // Authentication endpoints
  async login(credentials: ApiLoginInput): Promise<ApiResponse<string>> {
    console.log('Attempting login with generated client...', {
      hasEmail: !!credentials.email,
      hasUsername: !!credentials.username,
      hasPassword: !!credentials.password,
    });

    // Prepare login data - only send the field that's actually being used
    const loginData: ApiLoginInput = {
      password: credentials.password || '',
    };

    // Add either email or username, but not both empty strings
    if (credentials.email && credentials.email.trim()) {
      loginData.email = credentials.email.trim();
    } else if (credentials.username && credentials.username.trim()) {
      loginData.username = credentials.username.trim();
    } else {
      throw new Error('Either email or username must be provided');
    }

    console.log('Login request data:', {
      username: loginData.username,
      email: loginData.email,
      hasPassword: !!loginData.password,
    });

    const result = await this.wrapApiCall(() =>
      this.authApi.login({
        apiLoginInput: loginData,
      })
    );

    // Wait for cookies to be set after successful login
    if (!result.error) {
      await this.authMiddleware.waitForCookies();
      console.log(
        'Login successful, JWT cookie present:',
        this.authMiddleware.hasAuthCookies()
      );
      console.log('All cookies:', document.cookie);

      // If no cookies detected, store the login response as Bearer token
      if (!this.authMiddleware.hasAuthCookies()) {
        console.log(
          '🔑 No cookies detected after login. Storing login response as Bearer token...'
        );

        if (result.data) {
          console.log('✅ Storing login response as Bearer token');
          this.authMiddleware.storeBearerToken(result.data);
        } else {
          console.warn('⚠️ No token in login response');
        }
      } else {
        console.log('✅ JWT cookies detected, authentication complete');
      }
    } else {
      console.error('Login failed:', result.error, 'Status:', result.status);
    }

    return result;
  }

  async register(userData: ApiRegisterInput): Promise<ApiResponse<string>> {
    console.log('Attempting registration with generated client...');

    const registerPayload = {
      username: userData.username,
      email: userData.email,
    };

    const result = await this.wrapApiCall(() =>
      axios.post(`${this.apiBaseURL}/register`, registerPayload, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      })
    );

    return result;
  }

  async logout(): Promise<ApiResponse<string>> {
    const result = await this.wrapApiCall(() =>
      this.authApi.logout()
    );

    // Clear stored auth data after logout (even if logout fails)
    this.authMiddleware.clearAuthData();

    return result;
  }

  async getMe(): Promise<ApiResponse<ApiMeOutput>> {
    console.log('Attempting getMe...');
    console.log('- JWT cookie present:', this.authMiddleware.hasAuthCookies());
    console.log(
      '- Bearer token present:',
      !!this.authMiddleware.getBearerToken()
    );
    console.log(
      '- Has any authentication:',
      this.authMiddleware.hasAuthentication()
    );
    console.log('- Current cookies:', document.cookie);
    console.log('- Current domain:', window.location.hostname);
    console.log(
      '- Auth method:',
      this.authMiddleware.hasAuthCookies() ? 'cookies' : 'bearer-token'
    );

    const result = await this.wrapApiCall(() =>
      this.authApi.getMe()
    );

    // If successful, store Bearer token if present
    if (!result.error && result.data?.token) {
      console.log(
        '✅ Received Bearer token from /me response, storing for future requests'
      );
      this.authMiddleware.storeBearerToken(result.data.token);
    }

    if (result.status === 401) {
      console.log(
        '❌ getMe returned 401 - session invalid or not authenticated'
      );
      console.log(
        '- This might indicate the Bearer token is invalid or expired'
      );
    } else if (!result.error) {
      console.log('✅ getMe successful:', result.data);
    } else {
      console.error('❌ getMe failed:', result.error, 'Status:', result.status);
    }

    return result;
  }

  async requestOTP(input?: {
    email?: string;
    username?: string;
    action?: string;
  }): Promise<ApiResponse<void>> {
    if (input?.action === 'pwdreset') {
      return this.wrapApiCall(() =>
        axios.post(
          `${this.apiBaseURL}/password/reset/request`,
          {
            email: input.email,
            username: input.username,
          },
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
      );
    }

    if (input?.email || input?.username) {
      return this.wrapApiCall(() =>
        axios.get(`${this.apiBaseURL}/otp`, {
          withCredentials: true,
          params: {
            email: input.email,
            username: input.username,
          },
        })
      );
    }

    return this.wrapApiCall(() => this.authApi.requestOTP());
  }

  async verifyOTP(
    otpData: ApiVerifyOTPInput
  ): Promise<ApiResponse<ApiVerifyOTPOutput>> {
    return this.wrapApiCall(() =>
      axios.post(`${this.apiBaseURL}/otp`, otpData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      })
    );
  }

  async changePassword(
    data: ApiChangePasswordInput
  ): Promise<ApiResponse<string>> {
    return this.wrapApiCall(() =>
      this.authApi.changePassword({ apiChangePasswordInput: data })
    );
  }

  async regenerateToken(): Promise<ApiResponse<string>> {
    return this.wrapApiCall(() => this.authApi.regenerateToken());
  }

  async activateRegistration(data: {
    activation_token: string;
    password: string;
    password_confirm: string;
  }): Promise<ApiResponse<string>> {
    return this.wrapApiCall(() =>
      axios.post(`${this.apiBaseURL}/register/activate`, data, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      })
    );
  }

  async confirmPasswordReset(data: {
    action_token: string;
    password: string;
    password_confirm: string;
  }): Promise<ApiResponse<string>> {
    return this.wrapApiCall(() =>
      axios.post(`${this.apiBaseURL}/password/reset/confirm`, data, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      })
    );
  }

  // Requests endpoints (market research intake)
  async submitAppNeedsRequest(
    data: RequestsRequestAppPost
  ): Promise<ApiResponse<void>> {
    const requestsApi = await this.getRequestsApi();
    const fn = this.resolveAppRequest();
    if (typeof fn !== 'function') {
      return { error: 'App request API method not available', status: 0 };
    }
    return this.wrapApiCall(() =>
      fn.call(requestsApi, { requestsRequestAppPost: data })
    );
  }

  async submitHardwareNeedsRequest(
    data: RequestsRequestHardwarePost
  ): Promise<ApiResponse<void>> {
    const requestsApi = await this.getRequestsApi();
    const fn = this.resolveHardwareRequest();
    if (typeof fn !== 'function') {
      return { error: 'Hardware request API method not available', status: 0 };
    }
    return this.wrapApiCall(() =>
      fn.call(requestsApi, { requestsRequestHardwarePost: data })
    );
  }

  // New landing request endpoints (OpenAPI v0.0.1)
  async requestHardwareRent(
    data: RequestsRequestHardwareRentPost
  ): Promise<ApiResponse<void>> {
    const requestsApi = await this.getRequestsApi();
    const fn = this.resolveHardwareRentRequest();
    if (typeof fn !== 'function') {
      return { error: 'Hardware rent request API method not available', status: 0 };
    }
    return this.wrapApiCall(() =>
      fn.call(requestsApi, { requestsRequestHardwareRentPost: data })
    );
  }

  async requestHardwareRentOut(
    data: RequestsRequestHardwareRentOutPost
  ): Promise<ApiResponse<void>> {
    const requestsApi = await this.getRequestsApi();
    const fn = this.resolveHardwareRentOutRequest();
    if (typeof fn !== 'function') {
      return { error: 'Hardware rent out request API method not available', status: 0 };
    }
    return this.wrapApiCall(() =>
      fn.call(requestsApi, { requestsRequestHardwareRentOutPost: data })
    );
  }

  async requestLLMBusiness(
    data: RequestsRequestLLMBusinessPost
  ): Promise<ApiResponse<void>> {
    const requestsApi = await this.getRequestsApi();
    const fn = this.resolveLLMBusinessRequest();
    if (typeof fn !== 'function') {
      return { error: 'LLM business request API method not available', status: 0 };
    }
    return this.wrapApiCall(() =>
      fn.call(requestsApi, { requestsRequestLLMBusinessPost: data })
    );
  }

  async requestLLMPersonal(
    data: RequestsRequestLLMPersonalPost
  ): Promise<ApiResponse<void>> {
    const requestsApi = await this.getRequestsApi();
    const fn = this.resolveLLMPersonalRequest();
    if (typeof fn !== 'function') {
      return { error: 'LLM personal request API method not available', status: 0 };
    }
    return this.wrapApiCall(() =>
      fn.call(requestsApi, {
        requestsRequestLLMPersonalPost: data,
      })
    );
  }

  async requestAgentPlacement(
    data: RequestsRequestAgentPlacementPost
  ): Promise<ApiResponse<void>> {
    const requestsApi = await this.getRequestsApi();
    const fn = this.resolveAgentPlacementRequest();
    if (typeof fn !== 'function') {
      return { error: 'Agent placement API method not available', status: 0 };
    }
    return this.wrapApiCall(() =>
      fn.call(requestsApi, {
        requestsRequestAgentPlacementPost: data,
      })
    );
  }

  // Address/Payment endpoints
  async getAddress(): Promise<ApiResponse<ApiAddressGetOutput>> {
    return this.wrapApiCall(() => this.paymentsApi.getAddress());
  }

  async setAddress(
    addressData: ApiAddressPostInput
  ): Promise<ApiResponse<void>> {
    return this.wrapApiCall(() =>
      this.paymentsApi.setAddress({
        apiAddressPostInput: addressData,
      })
    );
  }

  async getTrialBalance(): Promise<ApiResponse<ApiTrialBalanceGetOutput>> {
    const fn = this.resolveTrialBalanceGet();
    return this.wrapApiCall(() => fn.call(this.paymentsApi));
  }

  async submitSurvey(
    data: ApiTrialBalancePostInput
  ): Promise<ApiResponse<void>> {
    const baseURL = process.env.REACT_APP_API_URL || 'https://api.swarmind.ai';
    return this.wrapApiCall(() =>
      axios.post<void>(`${baseURL}/survey`, data, {
        withCredentials: false,
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
      })
    );
  }

  // Machines endpoints
  async getMachines(): Promise<ApiResponse<MachinesMachines>> {
    return this.wrapApiCall(() => this.machinesApi.getMachines());
  }

  async getMachinesPricing(): Promise<
    ApiResponse<{ [key: string]: MachinesMachinePricing }>
  > {
    return this.wrapApiCall(() => this.machinesApi.getMachinesPricing());
  }

  async getP2PMachinesMap(): Promise<ApiResponse<{ [key: string]: string }>> {
    return this.wrapApiCall(() => this.machinesApi.getHeads());
  }

  // Store endpoints (lazy loaded)
  async getChats(namespace: string): Promise<ApiResponse<string[]>> {
    const storeApi = await this.getStoreApi();
    return this.wrapApiCall(() =>
      storeApi.getChats({ namespace })
    );
  }

  async getChatHistory(
    namespace: string,
    chat: string
  ): Promise<ApiResponse<StoreMessage[]>> {
    const storeApi = await this.getStoreApi();
    return this.wrapApiCall(() =>
      storeApi.getChatHistory({ namespace, chat })
    );
  }

  async updateChatHistory(
    namespace: string,
    chat: string,
    historyData: ApiStoreHistoryPostInput
  ): Promise<ApiResponse<void>> {
    const storeApi = await this.getStoreApi();
    return this.wrapApiCall(() =>
      storeApi.updateChatHistory({
        namespace,
        chat,
        apiStoreHistoryPostInput: historyData,
      })
    );
  }

  async deleteChat(
    namespace: string,
    chat: string
  ): Promise<ApiResponse<void>> {
    const storeApi = await this.getStoreApi();
    return this.wrapApiCall(() =>
      storeApi.deleteChat({ namespace, chat })
    );
  }

  // New: Store models endpoints (pairs and unique), and tags
  async getChatModelPairs(
    namespace: string,
    chat: string
  ): Promise<ApiResponse<Record<string, ModelsModel>>> {
    const storeApi = await this.getStoreApi();
    return this.wrapApiCall(() =>
      storeApi.getChatModelPairs({ namespace, chat })
    );
  }

  async getChatUniqueModels(
    namespace: string,
    chat: string
  ): Promise<ApiResponse<ModelsModel[]>> {
    const storeApi = await this.getStoreApi();
    return this.wrapApiCall(() =>
      storeApi.getChatUniqueModels({ namespace, chat })
    );
  }

  async getTags(): Promise<ApiResponse<string[]>> {
    const baseURL = process.env.REACT_APP_API_URL || 'https://api.swarmind.ai';
    const bearer = this.authMiddleware.getBearerToken();
    return this.wrapApiCall(() =>
      axios.get<string[]>(`${baseURL}/tags`, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
        },
      })
    );
  }

  // Huggingface endpoints (lazy loaded)
  async getRequestsCost(
    requestData: ApiRequestsCostInput
  ): Promise<ApiResponse<ApiRequestsCostOutput>> {
    const huggingfaceApi = await this.getHuggingfaceApi();
    return this.wrapApiCall(() =>
      huggingfaceApi.getRequestsCost({
        apiRequestsCostInput: requestData,
      })
    );
  }

  // LocalAI Proxy endpoints (lazy loaded)
  async getHeads(): Promise<ApiResponse<string[]>> {
    const localAIProxyApi = await this.getLocalAIProxyApi();
    return this.wrapApiCall(() => localAIProxyApi.getHeads());
  }

  // Utility methods
  hasAuthCookies(): boolean {
    return this.authMiddleware.hasAuthCookies();
  }

  hasAuthentication(): boolean {
    return this.authMiddleware.hasAuthentication();
  }

  clearAuthData(): void {
    this.authMiddleware.clearAuthData();
  }

  getBearerToken(): string | null {
    return this.authMiddleware.getBearerToken();
  }

  // Methods that throw ApiError instances for ErrorBoundary integration
  // These are useful for components that want to let errors bubble up to ErrorBoundary

  async loginWithThrow(credentials: ApiLoginInput): Promise<string> {
    console.log('Attempting login with generated client (throw mode)...');

    // Prepare login data - only send the field that's actually being used
    const loginData: ApiLoginInput = {
      password: credentials.password || '',
    };

    // Add either email or username, but not both empty strings
    if (credentials.email && credentials.email.trim()) {
      loginData.email = credentials.email.trim();
    } else if (credentials.username && credentials.username.trim()) {
      loginData.username = credentials.username.trim();
    } else {
      throw new Error('Either email or username must be provided');
    }

    const result = await this.wrapApiCallWithThrow(() =>
      this.authApi.login({ apiLoginInput: loginData })
    );

    // Wait for cookies to be set after successful login
    await this.authMiddleware.waitForCookies();
    console.log(
      'Login successful, JWT cookie present:',
      this.authMiddleware.hasAuthCookies()
    );

    // If no cookies detected, store the login response as Bearer token
    if (!this.authMiddleware.hasAuthCookies()) {
      console.log(
        '🔑 No cookies detected after login. Storing login response as Bearer token...'
      );
      this.authMiddleware.storeBearerToken(result);
    }

    return result;
  }

  async registerWithThrow(userData: ApiRegisterInput): Promise<string> {
    console.log(
      'Attempting registration with generated client (throw mode)...'
    );

    const registerPayload = {
      username: userData.username,
      email: userData.email,
    };

    const result = await this.wrapApiCallWithThrow(() =>
      axios.post(`${this.apiBaseURL}/register`, registerPayload, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      })
    );

    return result;
  }

  async getMeWithThrow(): Promise<ApiMeOutput> {
    console.log(
      'Attempting getMe (throw mode), JWT cookie present:',
      this.authMiddleware.hasAuthCookies()
    );

    // Try up to 3 times with a small delay between attempts
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const result = await this.wrapApiCallWithThrow(() =>
          this.authApi.getMe()
        );

        // Store Bearer token if present
        if (result.token) {
          console.log('Received Bearer token from /me response');
          this.authMiddleware.storeBearerToken(result.token);
        }

        console.log('getMe successful on attempt', attempt);
        return result;
      } catch (error) {
        // Don't retry auth failures
        if (error instanceof ApiError && error.isAuthError()) {
          console.log('getMe returned auth error - session invalid');
          throw error;
        }

        // For other errors, retry after a delay
        if (attempt < 3) {
          console.log(`getMe attempt ${attempt} failed, retrying in 500ms...`);
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          // Final attempt failed, throw the error
          throw error;
        }
      }
    }

    // This should never be reached, but TypeScript requires it
    throw new ApiError('Maximum retry attempts exceeded', 500);
  }

  async getMachinesWithThrow(): Promise<MachinesMachines> {
    return this.wrapApiCallWithThrow(() =>
      this.machinesApi.getMachines()
    );
  }

  async getAddressWithThrow(): Promise<ApiAddressGetOutput> {
    return this.wrapApiCallWithThrow(() =>
      this.paymentsApi.getAddress()
    );
  }

  async setAddressWithThrow(addressData: ApiAddressPostInput): Promise<void> {
    return this.wrapApiCallWithThrow(() =>
      this.paymentsApi.setAddress({
        apiAddressPostInput: addressData,
      })
    );
  }

  // Error handling utilities

  /**
   * Create an ApiError instance from a generic error
   * Useful for components that want to create consistent error objects
   */
  createApiError(
    message: string,
    statusCode: number = 500,
    code?: number,
    context?: Record<string, unknown>,
    isNetworkError: boolean = false,
    isValidationError: boolean = false
  ): ApiError {
    const apiError = new ApiError(
      message,
      statusCode,
      code?.toString(),
      context
    );
    apiError.isNetworkError = isNetworkError;
    apiError.isValidationError = isValidationError;
    return apiError;
  }

  /**
   * Transform any error into an ApiError instance
   * Useful for components that catch errors and want to re-throw them as ApiErrors
   */
  transformToApiError(error: unknown): ApiError {
    if (error instanceof ApiError) {
      return error;
    }

    const transformedError = transformGeneratedError(error);
    const apiError = new ApiError(
      transformedError.message,
      transformedError.status,
      transformedError.code?.toString(),
      transformedError.context,
      transformedError.validationErrors
    );
    apiError.isNetworkError = transformedError.isNetworkError;
    apiError.isValidationError = transformedError.isValidationError;
    return apiError;
  }
}

// Export singleton instance
export const generatedApiClient = new SwarmindApiClient();
export default generatedApiClient;
